import { clsx } from "clsx";
import { forwardRef, type TextareaHTMLAttributes, useId } from "react";

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    { label, error, hint, className, id: externalId, rows = 3, ...props },
    ref,
  ) {
    const generatedId = useId();
    const id = externalId ?? generatedId;
    const errorId = `${id}-error`;
    const hintId = `${id}-hint`;

    return (
      <div className={clsx("flex flex-col gap-1.5", className)}>
        {label && (
          <label htmlFor={id} className="text-sm font-medium text-sub">
            {label}
          </label>
        )}

        <textarea
          ref={ref}
          id={id}
          rows={rows}
          aria-invalid={error ? true : undefined}
          aria-describedby={
            [error && errorId, hint && hintId].filter(Boolean).join(" ") ||
            undefined
          }
          className={clsx(
            "w-full rounded-md border bg-panel px-3 py-2 text-sm text-main",
            "placeholder:text-faint",
            "transition-colors duration-150",
            "focus:outline-none focus:ring-1",
            "resize-y",
            error
              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
              : "border-edge hover:border-edge-hard focus:border-adara-500 focus:ring-adara-500",
          )}
          {...props}
        />

        {error && (
          <p id={errorId} className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        {hint && !error && (
          <p id={hintId} className="text-sm text-dim">
            {hint}
          </p>
        )}
      </div>
    );
  },
);
